import { useEffect, useState } from "react";
import { isR18ThreadTagName, resolveUploadPath } from "@/entities/thread";
import { getArchiveLinkAttributes } from "@/features/archive/archiveCatalogBehavior";
import type {
  ArchiveEvent,
  ArchiveEventNotifier,
} from "@/features/archive/mediators/archiveEvents";
import { isArchiveEvent } from "@/features/archive/mediators/archiveEvents";
import { createArchiveItemMediator } from "@/features/archive/mediators/archiveItemMediator";
import type { ArchiveThread } from "@/features/archive/types";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import { useSettingsStore } from "@/features/settings/hooks";
import type { EventNode, TypedEvent } from "@/shared/lib/componentHierarchy";
import { formatDateToMinute } from "@/shared/lib/date";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

export type ArchiveItemPresentation = {
  readonly archivedAtLabel: string;
  readonly imageUrl: string;
  readonly isR18Hidden: boolean;
  readonly isR18Revealed: boolean;
  readonly linkAttributes: React.AnchorHTMLAttributes<HTMLAnchorElement>;
  readonly node: EventNode<TypedEvent>;
  readonly onEvent: ArchiveEventNotifier;
  readonly textLines: number;
  readonly title: string;
};

export function useArchiveItemPresenter(
  thread: ArchiveThread,
  openInNewTab?: boolean,
): ArchiveItemPresentation {
  const catalogOpenInNewTab = useCatalogStore((state) => state.openInNewTab);
  const textLines = useCatalogStore((state) => state.textLines);
  const showR18 = useSettingsStore((state) => state.showR18);
  const [mediator] = useState(() => createArchiveItemMediator(thread.threadId));
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) => {
    if (!isArchiveEvent(event)) return "unhandled";
    if (
      event.type !== "archive/item-reveal-requested" &&
      event.type !== "archive/item-synchronized"
    ) {
      return "unhandled";
    }
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const itemState = useMediatorSnapshot(mediator);

  useEffect(() => {
    mediator.dispatch({
      type: "archive/item-synchronized",
      threadId: thread.threadId,
    });
  }, [mediator, thread.threadId]);

  const title = thread.opExcerpt || "(無題)";
  const isR18 = thread.tags.some((tag) => isR18ThreadTagName(tag.name));
  const isR18Revealed =
    itemState.threadId === thread.threadId && itemState.isR18Revealed;

  return {
    archivedAtLabel: formatDateToMinute(thread.archivedAt),
    imageUrl: resolveUploadPath(thread.thumbnailUrl || ""),
    isR18Hidden:
      isR18 && Boolean(thread.thumbnailUrl) && !showR18 && !isR18Revealed,
    isR18Revealed,
    linkAttributes: getArchiveLinkAttributes(
      openInNewTab ?? catalogOpenInNewTab,
    ),
    node,
    onEvent: (event: ArchiveEvent): ReturnType<ArchiveEventNotifier> =>
      node.dispatch(event).status,
    textLines,
    title,
  };
}
